import {
    createContext, useCallback, useContext, useRef, type FormEventHandler,
} from 'react';
import { ActionMeta } from 'react-select';
import { isEqual } from 'lodash-es';

const defaultCallback = () => {
    /* empty */
};

type FormValues = Record<string, FormDataEntryValue>;

interface FormContextInterface {
    values: FormValues,
    onSubmit: FormEventHandler<HTMLFormElement>,
    setFieldValue: <T>(fieldName: string, value: T) => void,
    setValues: <T>(values: T) => void,
    onChange: (event: unknown, action: ActionMeta<unknown>) => void,
    saving: boolean,
    formId: string,
    errors: Record<string, string>,
}

const defaultContext: FormContextInterface = {
    values: {},
    onSubmit: defaultCallback,
    setFieldValue: defaultCallback,
    setValues: defaultCallback,
    onChange: defaultCallback,
    saving: false,
    formId: '',
    errors: {},
};

export const FormContext = createContext<FormContextInterface | null>(null);

/**
 * Defines a callback that does not change after mount
 */
const useStaticCallback = <T extends (...args: any) => any>(callback: T): T => useCallback(callback, []); // eslint-disable-line

type MemoFunction = () => unknown;
type MemoKey = [Record<string, unknown>, string?];

const useDeepMemo = (memoFunction: MemoFunction, key: MemoKey) => {
    const ref = useRef<{ key: MemoKey, value: unknown }>({ key: [{}, ''], value: defaultCallback });

    if (!ref.current || !isEqual(key, ref.current.key)) {
        ref.current = { key, value: memoFunction() };
    }

    return ref.current.value;
};

export const useFormContext = () => useContext(FormContext);

/**
 * Retrieves the submitForm callback from the form context
 */
export const useFormSubmit = () => {
    const { onSubmit } = useFormContext() || defaultContext;

    return useStaticCallback(onSubmit);
};

/**
 * Retrieves the setFieldValue callback from the form context
 */
export const useSetFieldValue = () => {
    const { setFieldValue } = useFormContext() || defaultContext;

    return useStaticCallback(setFieldValue);
};

/**
 * Retrieves one or more values from the form context
 * Use dot notation to access nested properties
 */
export const useFormValues = <T = unknown>(property?: string): T => {
    const { values } = useFormContext() || defaultContext;

    return useDeepMemo(() => {
        if (property) {
            if (!values) {
                return null;
            }

            const buildValuesReturn = (valueObject: FormValues, propArray: string[]) => {
                let newValueObject = valueObject;
                const nextProperty = propArray.shift();

                if (nextProperty && Object.prototype.hasOwnProperty.call(newValueObject, nextProperty)) {
                    newValueObject = buildValuesReturn(newValueObject[nextProperty] as unknown as FormValues, propArray);
                }

                return newValueObject;
            };

            if (property.indexOf('.') > -1) {
                return buildValuesReturn(values, property.split('.'));
            }

            return values[property];
        }

        return values;
    }, [values, property]) as T;
};